import { db, Timestamp } from "./db.js";
import { ref } from "vue";
import { getMember } from "./teams.js";
import { createLog } from "./logging.js";

// ==================== CREATE ====================

async function createOfficeActionWithId(firmId, id, data) {
  const ref = db.doc(`firms/${firmId}/office-actions/${id}`);
  let log = {
    createdAt: new Timestamp.now(),
    applId: data.patentData.patentId,
    type: "create office action",
    officeActionId: id,
    status: "",
    errorMsg: "",
  };
  return ref
    .set(data, { merge: true })
    .then(() => {
      log.status = "success";
      createLog(firmId, log);
    })
    .catch((error) => {
      log.status = "error";
      log.errorMsg = error.message;
      createLog(firmId, log);
    });
}

async function createOfficeAction(firmId, data) {
  const ref = db.collection(`firms/${firmId}/office-actions`).doc();
  data.id = ref.id;
  let log = {
    createdAt: new Timestamp.now(),
    applId: data.patentData.patentId,
    type: "create office action",
    officeActionId: data.id,
    status: "",
    errorMsg: "",
  };
  return ref
    .set(data, { merge: true })
    .then(() => {
      log.status = "success";
      createLog(firmId, log);
    })
    .catch((error) => {
      log.status = "error";
      log.errorMsg = error.message;
      createLog(firmId, log);
    });
}

// ==================== READ ====================

async function getOfficeAction(firmId, officeActionId) {
  const oa = await db
    .doc(`firms/${firmId}/office-actions/${officeActionId}`)
    .get();
  return oa.exists ? oa.data() : null;
}

async function getOfficeActions(firmId) {
  const actions = [];
  await db
    .collection(`firms/${firmId}/office-actions`)
    .get()
    .then((querySnapshot) => {
      querySnapshot.forEach((doc) => {
        actions.push(doc.data());
      });
    });
  return actions;
}

async function getOfficeActionsOfPatent(firmId, patentId) {
  const actions = ref([]);
  await db
    .collection(`firms/${firmId}/office-actions`)
    .where("applId", "==", patentId)
    .get()
    .then((querySnaptshot) => {
      actions.value = querySnaptshot.docs.map((doc) => {
        return { id: doc.id, ...doc.data() };
      });
    });
  return actions;
}

async function getAttachments(firmId, officeActionId) {
  const attachments = ref([]);
  db.collection(
    `firms/${firmId}/office-actions/${officeActionId}/attachments`
  ).onSnapshot((snapshot) => {
    attachments.value = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  });
  return attachments;
}

async function getDocketingTasks(firmId, officeActionId) {
  const oa = await getOfficeAction(firmId, officeActionId);
  const querySnapshot = await db
    .collection(`firms/${firmId}/docketing`)
    .where("patentId", "==", oa.applId)
    .where("data.officeActionId", "==", officeActionId)
    .orderBy("deadline")
    .get();
  let docketingTasks = [];
  querySnapshot.forEach((doc) => {
    docketingTasks.push(doc.data());
  });
  return docketingTasks;
}

async function getPendingDocketingTasks(firmId, officeActionId) {
  const oa = await getOfficeAction(firmId, officeActionId);
  const querySnapshot = await db
    .collection(`firms/${firmId}/docketing`)
    .where("patentId", "==", oa.applId)
    .where("data.officeActionId", "==", officeActionId)
    .orderBy("deadline")
    .get();
  let docketingTasks = [];
  querySnapshot.forEach((doc) => {
    if (doc.data().status != "Completed")
      docketingTasks.push(doc.data());
  });
  return docketingTasks;
}

async function getOfficeActionsOfUser(firmId, userId) {
  const actions = ref([]);
  const member = await getMember(firmId, userId);

  return new Promise((resolve, reject) => {
    const collectionRef = db.collection(`firms/${firmId}/office-actions`);
    let query;

    if (member && (member.role == "admin")) {
      query = collectionRef;
    } else {
      query = collectionRef.where("assigned", "array-contains", userId);
    }

    const unsubscribe = query.onSnapshot(
      (querySnapshot) => {
        actions.value = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        resolve({ actions, unsubscribe }); // Resolve with tasks and unsubscribe function
      }, 
      reject
    );
  });
}

/*
async function getOfficeActionsOfUser(firmId, userId) {
  const actions = ref([]);
  const member = await getMember(firmId, userId);

  return new Promise((resolve, reject) => {
    const collectionRef = db.collection(`firms/${firmId}/office-actions`);

    if (member && member.role == "admin") {
      const unsubscribe = collectionRef.onSnapshot((querySnapshot) => {
        actions.value = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
      }, reject);
      resolve({ actions, unsubscribe });
    } else {
      getPatentsOfUser(firmId, userId).then(({ patents, unsubscribe: unsubscribePatents }) => {
        const unsubscribeActions = [];

        watch(patents, (newVal) => {
          const patentIds = newVal.map((patent) => patent.id);

          if (!patentIds.length) {
            actions.value = [];
            unsubscribeActions.forEach(unsub => unsub());
            unsubscribeActions.length = 0;
            return;
          }

          // Unsubscribe from the previous office actions listeners if they exist
          unsubscribeActions.forEach(unsub => unsub());
          unsubscribeActions.length = 0;

          // clear the oactions before adding new ones
          actions.value = [];

          // the while loop is to solve the problem that batch array cannot exceed 10
          while (patentIds.length) {
            const batch = patentIds.splice(0, 10);
            const unsubscribe = collectionRef
              .where("applId", "in", batch)
              .onSnapshot((querySnapshot) => {
                const newActions = querySnapshot.docs.map((doc) => ({
                  id: doc.id,
                  ...doc.data(),
                }));
                actions.value.push(...newActions);
              }, reject);
            unsubscribeActions.push(unsubscribe);
          }
        }, { immediate: true });

        resolve({
          actions,
          unsubscribe: () => {
            unsubscribePatents();
            unsubscribeActions.forEach(unsub => unsub());
          }
        });
      }).catch(reject);
    }
  });
}
  */
// ==================== UPDATE ====================

async function updateOfficeAction(firmId, officeActionId, data, transaction = null) {
  
  const oaRef = db.doc(`firms/${firmId}/office-actions/${officeActionId}`);
  if (transaction) {
    transaction.update(oaRef, data);
  } else {
    await oaRef.update(data);
  }
}

// ==================== DELETE ====================

async function archiveOfficeAction(firmId, officeActionId) {
  const ref = db.doc(`firms/${firmId}/office-actions/${officeActionId}`);
  return ref.set({archived: true}, {merge: true});
}

async function unArchiveOfficeAction(firmId, officeActionId) {
  const ref = db.doc(`firms/${firmId}/office-actions/${officeActionId}`);
  return ref.set({archived: false}, {merge: true});
}

function deleteAttachment(firmId, officeActionId, attachmentId) {
  return db
    .doc(
      `firms/${firmId}/office-actions/${officeActionId}/attachments/${attachmentId}`
    )
    .delete();
}

export {
  createOfficeAction,
  createOfficeActionWithId,
  getOfficeAction,
  getOfficeActions,
  getOfficeActionsOfPatent,
  getAttachments,
  getDocketingTasks,
  getPendingDocketingTasks,
  getOfficeActionsOfUser,
  updateOfficeAction,
  archiveOfficeAction,
  deleteAttachment,
  unArchiveOfficeAction,
};
